import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Landmark, CheckCircle2, ShieldCheck, Loader2, CreditCard, ExternalLink } from "lucide-react";
import { FlutterwavePayoutCard } from "@/components/FlutterwavePayoutCard";
import { KycSection } from "@/components/KycSection";
import { PayoutGuideCard } from "@/components/PayoutGuideCard";

export default function Payouts() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [stripe, setStripe] = useState(null);
  const [stripeBusy, setStripeBusy] = useState(false);
  const [stripeCountry, setStripeCountry] = useState(() => { const c = (user?.country || "").toUpperCase(); return STRIPE_COUNTRIES.some((x) => x.code === c) ? c : "US"; });
  const [banks, setBanks] = useState([]);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ business_name: "", bank_code: "", account_number: "" });
  const [resolvedName, setResolvedName] = useState("");
  const [resolving, setResolving] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      const [s, b, sc] = await Promise.all([
        api.get("/payouts/status"),
        api.get("/payouts/banks"),
        api.get("/payouts/stripe/status"),
      ]);
      setStatus(s.data);
      setBanks(b.data);
      setStripe(sc.data);
      if (params.get("stripe") === "return") {
        if (sc.data.connected) toast.success("Stripe account connected — card payments settle to you directly");
        else if (sc.data.started) toast.info("Stripe onboarding not finished yet — resume below to activate");
      }
      if (!s.data.connected && user && user !== false) {
        setForm((f) => ({ ...f, business_name: user.company || user.name || "" }));
      }
    })();
  }, [user, params]);

  const connectStripe = async () => {
    setStripeBusy(true);
    try {
      const { data } = await api.post("/payouts/stripe/connect", { origin_url: window.location.origin, country: stripeCountry });
      window.location.assign(data.onboarding_url);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
      setStripeBusy(false);
    }
  };

  const [resolveUnavailable, setResolveUnavailable] = useState(false);
  const tryResolve = useCallback(async (accountNumber, bankCode) => {
    if (accountNumber.length !== 10 || !bankCode) {
      setResolvedName("");
      return;
    }
    setResolving(true);
    setResolvedName("");
    setResolveUnavailable(false);
    try {
      const { data } = await api.post("/payouts/resolve", { account_number: accountNumber, bank_code: bankCode });
      setResolvedName(data.account_name);
    } catch (e) {
      setResolveUnavailable(true);
    } finally {
      setResolving(false);
    }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/payouts/connect", form);
      toast.success("Payout account connected — buyer payments now go straight to your bank");
      const { data: fresh } = await api.get("/payouts/status");
      setStatus(fresh);
      setEditing(false);
    } catch (e2) {
      toast.error(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  if (!status) return <div className="p-10 text-neutral-500">Loading…</div>;

  const showForm = !status.connected || editing;

  return (
    <div className="max-w-2xl mx-auto px-6 lg:px-10 py-12" data-testid="payouts-page">
      <div className="text-xs uppercase tracking-widest text-[#007AFF]">Payouts</div>
      <h1 className="mt-2 font-black tracking-tight text-4xl">Get paid directly</h1>
      <p className="mt-3 text-neutral-400 text-sm leading-relaxed max-w-lg">
        Connect a payment channel and every buyer payment settles{" "}
        <span className="text-white font-semibold">straight to you</span> — bank via Paystack,
        or your own Stripe account for international card payments.
        VISMARKS's fee — 0.5% per item sold — is deducted automatically at payment time — nothing to invoice, nothing owed later.
      </p>

      <div className="mt-8"><KycSection /></div>

      {status && !status.any_channel && (
        <div data-testid="payouts-required-notice" className="mt-8 rounded-md border border-[#FF9F0A]/30 bg-[#FF9F0A]/[0.06] px-4 py-3 text-xs text-neutral-300 flex items-start gap-2.5">
          <ShieldCheck className="h-4 w-4 text-[#FF9F0A] flex-shrink-0 mt-0.5" />
          <span>
            <span className="text-white font-semibold">Buyers can't purchase your products until this is set up.</span>{" "}
            We never hold your money — that's why a payout channel is required before your first sale.
          </span>
        </div>
      )}

      <div className="mt-10 flex items-center gap-2">
        <Landmark className="h-4 w-4 text-[#0BA4DB]" />
        <h2 className="font-black text-xl tracking-tight">Bank account — Paystack</h2>
      </div>

      {status.connected && !editing && (
        <div data-testid="payouts-connected-card" className="mt-5 rounded-xl border border-[#34C759]/40 bg-[#34C759]/[0.06] p-6">
          <div className="flex items-center gap-2 text-[#34C759]">
            <CheckCircle2 className="h-5 w-5" />
            <span className="font-bold text-white">Payout account connected</span>
          </div>
          <dl className="mt-4 grid grid-cols-2 gap-4 text-sm">
            <Item label="Business" value={status.business_name} />
            <Item label="Bank" value={status.bank_name} />
            <Item label="Account" value={`•••• ${status.account_last4}`} />
            <Item label="Account name" value={status.account_name} />
          </dl>
          <p className="mt-4 text-xs text-neutral-500">
            Buyers can purchase your products. Sales settle to this account on Paystack's standard settlement schedule.
          </p>
          <Button
            variant="outline"
            data-testid="payouts-update-btn"
            onClick={() => { setEditing(true); setForm({ business_name: status.business_name || "", bank_code: "", account_number: "" }); setResolvedName(""); }}
            className="mt-4 rounded-full bg-transparent border-white/20 text-white hover:bg-white/5"
          >
            Update bank details
          </Button>
        </div>
      )}

      {showForm && (
        <form onSubmit={submit} className="mt-6 space-y-5">
          <Field label="Business / payout name">
            <Input
              required
              data-testid="payouts-business-name"
              value={form.business_name}
              onChange={(e) => setForm({ ...form, business_name: e.target.value })}
              className="bg-[#121212] border-[#27272A]"
              placeholder="Acme Trading Co."
            />
          </Field>
          <Field label="Bank">
            <select
              required
              data-testid="payouts-bank-select"
              value={form.bank_code}
              onChange={(e) => {
                const bank_code = e.target.value;
                setForm({ ...form, bank_code });
                tryResolve(form.account_number, bank_code);
              }}
              className="w-full h-10 rounded-md bg-[#121212] border border-[#27272A] px-3 text-sm text-white focus:outline-none focus:border-[#007AFF]"
            >
              <option value="">Select your bank…</option>
              {banks.map((b) => (
                <option key={`${b.code}-${b.name}`} value={b.code}>{b.name}</option>
              ))}
            </select>
          </Field>
          <Field label="Account number (10 digits)">
            <Input
              required
              inputMode="numeric"
              maxLength={10}
              data-testid="payouts-account-number"
              value={form.account_number}
              onChange={(e) => {
                const account_number = e.target.value.replace(/\D/g, "").slice(0, 10);
                setForm({ ...form, account_number });
                tryResolve(account_number, form.bank_code);
              }}
              className="bg-[#121212] border-[#27272A] font-mono tracking-widest"
              placeholder="0123456789"
            />
          </Field>

          {resolving && (
            <div className="text-xs text-neutral-500 flex items-center gap-2">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Verifying account…
            </div>
          )}
          {resolvedName && (
            <div data-testid="payouts-resolved-name" className="rounded-md border border-[#34C759]/40 bg-[#34C759]/10 px-3 py-2 text-sm">
              <span className="text-[10px] uppercase tracking-widest text-[#34C759] block">Account verified</span>
              <span className="font-semibold text-white">{resolvedName}</span>
            </div>
          )}
          {resolveUnavailable && !resolvedName && (
            <div data-testid="payouts-resolve-unavailable" className="rounded-md border border-white/10 bg-white/[0.03] px-3 py-2 text-xs text-neutral-400">
              Instant name check unavailable right now — no problem, your bank details will be validated when you connect.
            </div>
          )}

          <Button
            type="submit"
            disabled={busy}
            data-testid="payouts-connect-btn"
            className="w-full bg-white text-black hover:bg-neutral-200 rounded-full h-11 font-semibold"
          >
            <Landmark className="h-4 w-4 mr-2" />
            {busy ? "Connecting…" : status.connected ? "Save new bank details" : "Connect payout account"}
          </Button>
          {editing && (
            <Button
              type="button"
              variant="ghost"
              onClick={() => setEditing(false)}
              className="w-full text-neutral-400 hover:text-white hover:bg-white/5"
            >
              Cancel
            </Button>
          )}
        </form>
      )}

      <div className="mt-12 pt-8 border-t border-white/10">
        <div className="flex items-center gap-2">
          <CreditCard className="h-4 w-4 text-[#635BFF]" />
          <h2 className="font-black text-xl tracking-tight">Stripe — international card payments</h2>
        </div>
        <p className="mt-2 text-sm text-neutral-400 max-w-lg">
          Connect your own Stripe account and card payments from buyers worldwide settle{" "}
          <span className="text-white font-semibold">directly to your Stripe balance</span>, with
          Vismarks's fee taken upfront.
        </p>
        {stripe?.connected ? (
          <div data-testid="stripe-connected-card" className="mt-5 rounded-xl border border-[#34C759]/40 bg-[#34C759]/[0.06] p-5">
            <div className="flex items-center gap-2 text-[#34C759]">
              <CheckCircle2 className="h-5 w-5" />
              <span className="font-bold text-white">Stripe account connected</span>
            </div>
            <p className="mt-2 text-xs text-neutral-500 font-mono">{stripe.account_id}</p>
            <p className="mt-1 text-xs text-neutral-500">
              Card payments settle to your Stripe balance and pay out to your bank on Stripe's schedule.
            </p>
          </div>
        ) : (
          <div className="mt-5 flex flex-col sm:flex-row gap-3 sm:items-center">
            {!stripe?.started && (
              <select
                value={stripeCountry}
                onChange={(e) => setStripeCountry(e.target.value)}
                data-testid="stripe-country-select"
                className="h-11 rounded-md bg-[#121212] border border-[#27272A] px-3 text-sm text-white focus:outline-none focus:border-[#635BFF]"
              >
                {STRIPE_COUNTRIES.map((c) => (
                  <option key={c.code} value={c.code}>{c.name}</option>
                ))}
              </select>
            )}
            <Button
              onClick={connectStripe}
              disabled={stripeBusy}
              data-testid="stripe-connect-btn"
              className="bg-[#635BFF] text-white hover:bg-[#5148f0] rounded-full h-11 px-6 font-semibold"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              {stripeBusy ? "Opening Stripe…" : stripe?.started ? "Resume Stripe onboarding" : "Connect with Stripe"}
            </Button>
          </div>
        )}
      </div>

      <FlutterwavePayoutCard defaultBusinessName={(user && user !== false && (user.company || user.name)) || ""} />
      <PayoutGuideCard />
    </div>
  );
}

const STRIPE_COUNTRIES = [
  { code: "US", name: "United States" },
  { code: "GB", name: "United Kingdom" },
  { code: "CA", name: "Canada" },
  { code: "AU", name: "Australia" },
  { code: "DE", name: "Germany" },
  { code: "FR", name: "France" },
  { code: "ES", name: "Spain" },
  { code: "IT", name: "Italy" },
  { code: "NL", name: "Netherlands" },
  { code: "IE", name: "Ireland" },
  { code: "SG", name: "Singapore" },
  { code: "HK", name: "Hong Kong" },
  { code: "JP", name: "Japan" },
  { code: "AE", name: "United Arab Emirates" },
  { code: "BR", name: "Brazil" },
  { code: "MX", name: "Mexico" },
  { code: "IN", name: "India" },
  { code: "MY", name: "Malaysia" },
  { code: "TH", name: "Thailand" },
  { code: "NZ", name: "New Zealand" },
  { code: "PT", name: "Portugal" },
  { code: "PL", name: "Poland" },
  { code: "SE", name: "Sweden" },
  { code: "NO", name: "Norway" },
  { code: "DK", name: "Denmark" },
  { code: "CH", name: "Switzerland" },
  { code: "BE", name: "Belgium" },
  { code: "AT", name: "Austria" },
];

function Item({ label, value }) {
  return (
    <div>
      <dt className="text-[10px] uppercase tracking-widest text-neutral-500">{label}</dt>
      <dd className="mt-0.5 text-white font-medium">{value || "—"}</dd>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-widest text-neutral-400">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}
